// Vue
import { i18n } from "@/i18n"

// Types
import type {
  IEstablishmentGeneral,
  IEstablishmentContact,
  IEstablishmentHours,
  IAttribute,
  IEstablishmentSpecific,
  ILanguage,
  IPayment,
  IEstablishment,
  IPeriodHours,
} from "@/types/Establishment.types"
import type { ICategory } from "@/types/Establishment.types"
import type { IMediaAlbumRules, MediaAlbum, MediaPayload } from "@/types/Media.types"

// Stores
import { defineStore } from "pinia"
import { useAccountStore } from "./account"
import { useAuthenticationStore } from "./authentication"
import { useAlertStore } from "./alert"
import { useTrackingStore } from "./tracking"

// Data
import { AlertType } from "@/static/Alert"

// Services
import EstablishmentDataService from "@/services/EstablishmentDataService"
import TranslationDataService from "@/services/TranslationDataService"
import MediaDataService from "@/services/MediaDataService"
import { type CommunicationCode, freemiumPlans } from "@/static/Establishment"
import UberallDataService from "@/services/UberallDataService"
import { useLocalDiffuserStore } from "./localDiffuser"
import { useNotificationsStore } from "./notifications"
import { useDashboardStore } from "./dashboard"
import { useNotificationCenterStore } from "./notificationCenter"
import moment from "moment/moment";
import { getAccessContract, getAccessPlanCode } from "@/utils/contract"

export const useEstablishmentStore = defineStore("establishment", {
  state: () => ({
    count: 0,

    /**
     * is the save bar shown ?
     * @type {boolean}
     */
    showSaveButton: false,

    /**
     * current form active tab index
     * @type {number}
     */
    currentTabIndex: 0,

    /**
     * form max index (number of tabs -1 cause index begin at 0)
     * @type {number}
     */
    maxIndex: 5,

    /**
     * form max index (number of tabs -1 cause index begin at 0)
     * @type {number}
     */
    configMaxIndex: 1,

    /**
     * is the current establishment form is in config mode ?
     * @type {boolean}
     */
    isConfigurationForm: false,

    /**
     * current selected establishment from endpoint (same for configuration and app form)
     * @type {IEstablishment}
     */
    currentEstablishment: {} as IEstablishment,

    /**
     * current selected establishment local updatable property
     * have this property locally prevent multiple request to database to get full establishment
     */
    currentEstablishmentIsUpdatable: true,

    /**
     * Indique si le SALE rattaché à l'établissement est d'un compte partenaire
     * null = pas encore chargé, true = partenaire, false = non-partenaire (Uska)
     * @type {boolean | null}
     */
    isSalePartnerAccount: null as boolean | null,

    /**
     * true si le compte de l'établissement a un sale_user_id (commercial assigné), hors fallback API.
     * null = pas encore chargé depuis /sale-user
     */
    accountLinkedToSale: null as boolean | null,

    /**
     * Product plans disponibles pour le compte du SALE rattaché
     * @type {any[]}
     */
    saleProductPlans: [] as any[],

    /**
     * Indique si l'établissement a déjà des licences associées à son compte
     * @type {boolean}
     */
    hasEstablishmentLicenses: false,

    /**
     * Date de formation du compte de l'établissement (pas l'utilisateur connecté)
     * @type {string | null}
     */
    establishmentAccountTrainingDate: null as string | null,

    /**
     * Autorisation du virement bancaire pour le compte de l'établissement
     * @type {boolean | null}
     */
    allowWireTransfer: null as boolean | null,

    /**
     * Autorisation du paiement en 3 fois sans frais pour le compte de l'établissement
     * @type {boolean | null}
     */
    paymentFacilities: null as boolean | null,

    /**
     * Facturation externe sur le compte de l'établissement (checkout sans tarifs / paiement Uska)
     * @type {boolean | null}
     */
    accountExternalBilling: null as boolean | null,

    /**
     * URL personnalisée des CGU pour le compte du SALE rattaché à l'établissement
     * @type {string | null}
     */
    saleTermsUrl: null as string | null,

    /**
     * current selected establishment in form tabs objects
     * @type {object}
     */
    form: {
      general: {} as IEstablishmentGeneral,
      contacts: {} as IEstablishmentContact,
      platforms: {} as IEstablishmentContact,
      hours: {
        open_24_7: false,
        days: [],
        exceptional_periods: [],
      } as IEstablishmentHours,
      specific: {} as IEstablishmentSpecific,
    },

    /**
     * list of available categories
     * @type {ICategory[]}
     */
    categories: [] as ICategory[],

    /**
     * list of all attributes
     * @type {IAttribute[]}
     */
    attributes: [] as IAttribute[],

    /**
     * list of available attributes (depends on current establishment main category)
     * @type {IAttribute[]}
     */
    availableAttributes: [] as IAttribute[],

    /**
     * list of all payment options
     * @type {IPayment[]}
     */
    paymentOptions: [] as IPayment[],

    /**
     * list of available languages
     * @type {ILanguage[]}
     */
    languages: [] as ILanguage[],

    /**
     * list of available languages
     * @type {ILanguage[]}
     */
    lodgingLanguages: [] as ILanguage[],

    /**
     * list of available albums for selected main category
     * @type {IMediaAlbumRules[]}
     */
    albums: [] as IMediaAlbumRules[],

    albumsLoading: false,

    /**
     * is the current form tab loading ?
     * @type {boolean}
     */
    isLoading: false,

    /**
     * is the form in syncing ?
     * @type {boolean}
     */
    inSyncing: false,

    /**
     * is the current establishment loading ?
     * @type {boolean}
     */
    formLoaded: false,
  }),

  getters: {
    isCurrentEstablishmentActiv: (state): {} => {
      const contract = getAccessContract(state.currentEstablishment)

      if (!contract) {
        return false
      }

      const now = moment()
      const validFrom = contract.valid_from ? moment(contract.valid_from) : null
      const validThrough = contract.valid_through ? moment(contract.valid_through) : null

      const hasStarted = !validFrom || now.isSameOrAfter(validFrom, "day")
      const notExpired = !validThrough || now.isSameOrBefore(validThrough, "day")

      const hasValidContract = hasStarted && notExpired

      // Si pas de contrat valide, retourner false
      if (!hasValidContract) {
        return false
      }

      // Vérifier les conditions de paiement si la souscription et le paiement existent
      const subscription = state.currentEstablishment.current_subscription
      const payment = state.currentEstablishment.current_payment

      // Si pas de souscription ou pas de paiement, on considère l'établissement comme actif
      // (cas des anciens établissements ou paiements hors système)
      if (!subscription || !payment) {
        return hasValidContract
      }

      // Si le paiement est en litige (disputed), bloquer l'accès immédiatement
      if (payment.status === 'disputed') {
        return false
      }

      const paymentDueDate = payment?.metadata?.due_date || payment?.stripe_invoice_due_date || payment?.due_date
      const dueDate = paymentDueDate ? moment(paymentDueDate) : null
      const isDueDateExceededBy5Days = dueDate?.isValid() ? now.diff(dueDate, 'days') >= 5 : false

      // Si le paiement est en erreur (failed ou insufficient_funds), bloquer l'accès
      // dès que la due_date est dépassée d'au moins 5 jours
      if ((payment.status === 'failed' || payment.status === 'insufficient_funds') && isDueDateExceededBy5Days) {
        return false
      }

      // Si le paiement est en attente (pending) et que la due_date est dépassée
      // d'au moins 5 jours, l'établissement n'est pas actif
      if (payment.status === 'pending' && isDueDateExceededBy5Days) {
        return false
      }

      return hasValidContract
    },

    isCurrentEstablishmentLodging: (state): {} => {
      return state.currentEstablishment.is_lodging
    }
  },

  actions: {
    /**
     * Clear all establishment data
     */
    clearCheckoutSaleContext() {
      this.saleProductPlans = []
      this.isSalePartnerAccount = null
      this.accountLinkedToSale = null
      this.saleTermsUrl = null
      this.allowWireTransfer = null
      this.paymentFacilities = null
      this.accountExternalBilling = null
    },

    async prepareForCheckoutEstablishment(establishmentId: string | number): Promise<void> {
      this.clearCheckoutSaleContext()
      await this.getCurrentEstablishment(Number(establishmentId))
    },

    clear() {
      this.showSaveButton = false
      this.isConfigurationForm = false
      this.currentEstablishment = {} as IEstablishment
      this.form = {
        general: {} as IEstablishmentGeneral,
        contacts: {} as IEstablishmentContact,
        platforms: {} as IEstablishmentContact,
        hours: {
          open_24_7: false,
          days: [],
          exceptional_periods: [],
        } as IEstablishmentHours,
        specific: {} as IEstablishmentSpecific,
      }
      this.isLoading = false
      this.formLoaded = false
      this.isSalePartnerAccount = null
      this.accountLinkedToSale = null
      this.hasEstablishmentLicenses = false
      this.establishmentAccountTrainingDate = null
      this.allowWireTransfer = null
      this.paymentFacilities = null
      this.accountExternalBilling = null
      this.saleTermsUrl = null
      this.saleProductPlans = []
      this.albums = []
      this.albumsLoading = false
    },

    changeCurrentEstablishment(newEstablishment: IEstablishment, options?: { skipRouteSync?: boolean }) {
      return this.initEstablishment(newEstablishment.id.toString(), () => {
        const notificationsStore = useNotificationsStore()
        const dashboardStore = useDashboardStore()
        const notificationCenterStore = useNotificationCenterStore()

        void notificationsStore.getNotifications()
        void notificationCenterStore.load(true)
        void dashboardStore.getProfil()

        const accountStore = useAccountStore()
        const router = this.router
        const currentRoute = router.currentRoute.value

        // Exclure certaines routes : évite redirections ET accountStore.reload (qui force
        // un remount via :key dans App.vue → boucle infinie avec loadEstablishmentDetails)
        const excludedRoutes = [
          'checkout-flow',
          'sales-contracts-management',
          'sales-establishment-details',
          'assistant',
          'local-assistant',
          'dialogue-local-assistant',
        ]
        if (excludedRoutes.includes(currentRoute.name as string)) {
          return
        }

        accountStore.reload += 1

        if (options?.skipRouteSync) {
          return
        }

        // Vérifier si c'est un établissement DEMO basé sur le contrat en cours
        const isDemoEstablishment = getAccessPlanCode(this.currentEstablishment) === 'DEMO'
        
        // Routes autorisées pour les établissements DEMO
        const demoRoutes = [
          'editorial-calendar',
          'demo-review-responses',
          'knowledge-base-brand-profile',
          'knowledge-base-recurring-situations',
          'knowledge-base-personas',
          'knowledge-base-activity-catalogs',
          'knowledge-base-signatures',
        ]
        
        if (isDemoEstablishment) {
          // Si c'est un établissement DEMO et qu'on n'est pas sur une route autorisée
          if (!demoRoutes.includes(currentRoute.name as string)) {
            router.push({ name: 'editorial-calendar' })
          }
        } else if (currentRoute.name === 'pilotage-empty-state') {
          router.push({ name: 'home' })
        } else {
          // Si c'est un établissement premium et qu'on est sur une route DEMO
          // Ne pas rediriger automatiquement depuis demo-review-responses (l'utilisateur peut y être pour une raison valable)
          if (demoRoutes.includes(currentRoute.name as string) && currentRoute.name !== 'demo-review-responses') {
            router.push({ name: 'home' })
          }
        }
      })
    },

    getIcon(establishment: IEstablishment) {
      if (establishment && establishment.name) {
        const baseUrl = "https://api.dicebear.com/7.x/initials/svg?"
        const size = "size=32"
        // const radius = "&radius=50"
        const font = "&fontFamily=Trebuchet%20MS&fontSize=61&fontWeight=600"

        const unique = establishment.id + establishment.name.length

        return baseUrl + size + font + "&seed=" + establishment.name + establishment.id + unique
      } else {
        return null
      }
    },

    /**
     * Get the current authenticated account establishments list
     * In configuration, user should have only one establishment, so we take only the first one
     * The request is called only the first time, not at all changes
     * @param id
     * @param successCallback
     */
    initEstablishment(id?: string, successCallback?: () => void) {
      // Réinitialiser l'état de chargement à chaque init pour éviter les flashes
      // liés à un état persistant (refresh navigateur / restore store).
      this.formLoaded = false
      this.albums = []
      this.albumsLoading = false

      if (id) {
        return this.getCurrentEstablishment(Number(id), () => {
          this.loadAlbums()
          this.formLoaded = true
          if (successCallback) {
            successCallback()
          }
        })
      } else {
        const account = useAccountStore()
        return account.getEstablishments((establishments) => {
          if (establishments[0]) {
            this.currentEstablishment = establishments[0]
            this.currentEstablishmentIsUpdatable = this.currentEstablishment.is_updatable

            this.syncLocalDiffuserBranding()

            this.formLoaded = true
            if (successCallback) {
              successCallback()
            }
          } else if (account.hasDestinationProviders) {
            // Compte Dialogue uniquement (fiches Dahub, sans établissement) : attendu, pas d'alerte.
            this.formLoaded = true
            if (successCallback) {
              successCallback()
            }
          } else {
            const alertStore = useAlertStore()
            alertStore.push(AlertType.error, i18n.global.t("common.errors.no_establishment"), "initEstablishment")
          }
        })
      }
    },

    /**
     * Get all establishment data by id
     * @param id
     * @param successCallback
     */
    async getCurrentEstablishment(id?: number, successCallback?: () => void) {
      let establishmentId = id ?? this.currentEstablishment.id

      const auth = useAuthenticationStore()
      if (establishmentId) {
        await EstablishmentDataService.getEstablishmentById(establishmentId, auth.currentHeaders).then(
          (res) => {
            if (res.data.data) {
              this.currentEstablishment = JSON.parse(JSON.stringify(res.data.data))
              this.currentEstablishmentIsUpdatable = this.currentEstablishment.is_updatable

              this.syncLocalDiffuserBranding()

              if (successCallback) {
                successCallback()
              }
            }
          },
          (e) => {
            const alertStore = useAlertStore()

            switch (e.response.status) {
              case 401:
                break
              default:
                alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                break
            }
          }
        )
      } else {
        const alertStore = useAlertStore()
        this.router.push({ name: "auth" })
        auth.logout()
        alertStore.push(AlertType.info, "Votre session a expirée")
      }
    },

    syncLocalDiffuserBranding() {
      const accountStore = useAccountStore()

      const hasAtLeastOneLocalDiffuser = accountStore.establishments.some(
        (establishment) => (establishment.local_diffusers?.length ?? 0) > 0
      )

      if (accountStore.establishments.length > 0 && hasAtLeastOneLocalDiffuser) {
        const localDiffuserStore = useLocalDiffuserStore()
        localDiffuserStore.logo()
      }
    },

    /**
     * Refresh local establishment data
     */
    async refreshCurrentEstablishment(callback?: () => void) {
      const establishmentId = this.currentEstablishment.id

      this.isLoading = true

      await this.getCurrentEstablishment(establishmentId, callback)
    },

    /**
     * Synchronize all establishment data in all diffusers list
     * The data is sent to uberall for sync, but the data will not be in the diffuser list right now
     * It can be longer, it can be stop by a problem... Go to listing view to now the diffusers status sync
     * @param establishmentId
     * @param successCallback
     * @param errorCallback
     */
    sync(establishmentId: number, successCallback?: () => void, errorCallback?: (code: string|null) => void) {
      this.inSyncing = true

      const authStore = useAuthenticationStore()

      UberallDataService.syncLocation(establishmentId, authStore.currentHeaders).then(
        () => {
          this.inSyncing = false
          useTrackingStore().trackDataSync(establishmentId)
          if (successCallback) {
            successCallback()
          }
        },
        (e) => {
          const alertStore = useAlertStore()
          switch (e.response.status) {
            case 401:
              break
            default:
              break
          }
          this.inSyncing = false
          if (errorCallback) {
            errorCallback(e.response.data.data)
          }
        }
      )
    },

    /**
     * Update the is_updatable property to know if the current establishment is ready for sync
     * @param isUpdatable
     */
    updatable(isUpdatable: boolean, successCallback?: () => void) {
      const authStore = useAuthenticationStore()

      EstablishmentDataService.updateEstablishmentUpdatable(
        this.currentEstablishment.id,
        isUpdatable,
        authStore.currentHeaders
      ).then(
        (res) => {
          this.currentEstablishmentIsUpdatable = isUpdatable
          if (successCallback) {
            setTimeout(() => {
              successCallback()
            }, 400)
          }
        },
        (e) => {
          const alertStore = useAlertStore()
          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    setFirstConnexion() {
      if (this.currentEstablishment.id) {
        EstablishmentDataService.setFirstConnexionDate(this.currentEstablishment.id).then(
          (res) => {
            //
          },
          (e) => {
            const alertStore = useAlertStore()
            switch (e.response.status) {
              case 401:
                break
              default:
                alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                break
            }
          }
        )
      }
    },

    initEstablishmentWithGoogleData() {
      EstablishmentDataService.initWithGoogleData(this.currentEstablishment.id)
    },

    // ========= GENERAL =========

    /**
     * Save all general tab form (in uberall and uska)
     * @param showSuccess show alert if request is successfully complete
     */
    saveGeneral(showSuccess: boolean, callback: () => void) {
      this.isLoading = true

      if (!this.form.general.long_description) {
        this.form.general.long_description = ""
      }

      if (!this.form.general.short_description) {
        this.form.general.short_description = ""
      }

      const authStore = useAuthenticationStore()
      const alertStore = useAlertStore()

      EstablishmentDataService.updateEstablishment(this.form.general, authStore.currentHeaders).then(
        () => {
          useTrackingStore().trackDataUpdate(this.currentEstablishment.id, "general")
          this.updatable(true, callback)
          this.currentEstablishment.name = this.form.general.name

          if (showSuccess) {
            alertStore.push(
              AlertType.success,
              i18n.global.t("establishments/formulaire.generic_informations.success_alert_message"),
              i18n.global.t("establishments/formulaire.generic_informations.tab_title")
            )
          }
        },
        (e) => {
          this.isLoading = false
          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    loadCategories(successCallback?: () => void) {
      let locale = i18n.global.locale.value
      if (locale === undefined) {
        locale = import.meta.env.VITE_APP_I18N_LOCALE
      }

      TranslationDataService.fetchCategories().then(
        (response) => {
          if (response.data[locale]) {
            this.categories = response.data[locale]
            if (successCallback) {
              successCallback()
            }
          }
        },
        (e) => {
          const alertStore = useAlertStore()
          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    // ========= SPECIFIC =========

    /**
     * Save all specific tab form (in uberall and uska)
     * @param showSuccess show alert if request is successfully complete
     */
    saveSpecific(showSuccess: boolean, callback: () => void) {
      this.isLoading = true

      const alertStore = useAlertStore()

      EstablishmentDataService.updateSpecific(this.form.specific).then(
        () => {
          useTrackingStore().trackDataUpdate(this.currentEstablishment.id, "specific")
          this.updatable(true, callback)

          if (showSuccess) {
            alertStore.push(
              AlertType.success,
              i18n.global.t("establishments/formulaire.specific_informations.success_alert_message"),
              i18n.global.t("establishments/formulaire.specific_informations.tab_title")
            )
          }
        },
        (e) => {
          this.isLoading = false
          const authStore = useAuthenticationStore()
          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    loadAttributes() {
      let locale = i18n.global.locale.value
      if (locale === undefined) {
        locale = import.meta.env.VITE_APP_I18N_LOCALE
      }

      TranslationDataService.fetchAttributes().then(
        (response) => {
          if (response.data[locale]) {
            this.attributes = response.data[locale]
          }
        },
        (e) => {
          const alertStore = useAlertStore()

          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    loadLanguages() {
      let locale = i18n.global.locale.value
      if (locale === undefined) {
        locale = import.meta.env.VITE_APP_I18N_LOCALE
      }

      TranslationDataService.fetchLanguages().then(
        (response) => {
          if (response.data[locale]) {
            this.languages = response.data[locale]
          }
        },
        (e) => {
          const authStore = useAuthenticationStore()
          const alertStore = useAlertStore()

          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    loadLodgingLanguages() {
      let locale = i18n.global.locale.value
      if (locale === undefined) {
        locale = import.meta.env.VITE_APP_I18N_LOCALE
      }

      TranslationDataService.fetchLodgingLanguages().then(
        (response) => {
          if (response.data[locale]) {
            this.lodgingLanguages = response.data[locale]
          }
        })
    },

    loadPaymentOptions() {
      let locale = i18n.global.locale.value
      if (locale === undefined) {
        locale = import.meta.env.VITE_APP_I18N_LOCALE
      }

      TranslationDataService.fetchPaymentOptions().then(
        (response) => {
          if (response.data[locale]) {
            this.paymentOptions = response.data[locale]
          }
        },
        (e) => {
          const authStore = useAuthenticationStore()
          const alertStore = useAlertStore()

          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    loadAvailableAttributes(establishmentId: number) {
      const alertStore = useAlertStore()

      EstablishmentDataService.getAttributes(establishmentId).then(
        (res) => {
          if (res.data.success) {
            this.availableAttributes = res.data.data
          } else {
            this.availableAttributes = []
            alertStore.push(
              AlertType.error,
              i18n.global.t("common.errors.default"),
              i18n.global.t("establishments/formulaire.specific_informations.attributes.label")
            )
          }
        },
        (e) => {
          const authStore = useAuthenticationStore()
          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    // ========= CONTACT & PLATEFORMS =========

    /**
     * Save all communications (platforms tab and contact tab) (in uberall and uska)
     * @param showSuccess show alert if request is successfully complete
     */
    saveCommunications(showSuccess: boolean, callback: () => void) {
      this.isLoading = true

      const alertStore = useAlertStore()

      EstablishmentDataService.updateContact(this.form.contacts).then(
        () => {
          useTrackingStore().trackDataUpdate(this.currentEstablishment.id, "communications")
          this.updatable(true, callback)

          if (showSuccess) {
            alertStore.push(AlertType.success, i18n.global.t("establishments/formulaire.contact.success_alert_message"))
          }
        },
        (e) => {
          this.isLoading = false
          const authStore = useAuthenticationStore()
          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    updateCom(event: { target: { value: any } }, code: CommunicationCode) {
      const val = event.target.value
      const communication = this.form.contacts.communications.find((com) => com.code === code)

      if (communication !== undefined) {
        communication.value = val
      } else {
        this.form.contacts.communications.push({
          code: code,
          value: val,
        })
      }
    },

    setCommunication(value: string, code: CommunicationCode) {
      const communication = this.form.contacts.communications.find((com) => com.code === code)

      if (communication !== undefined) {
        communication.value = value
      } else {
        this.form.contacts.communications.push({
          code: code,
          value: value,
        })
      }
    },

    getCommunicationValueFromCode(code: CommunicationCode) {
      const communications = this.currentEstablishment.communications
      let value = null

      if (communications) {
        const com = communications.find((_com) => _com.code === code)

        if (com) {
          value = com.value
        }
      }

      return value
    },

    // ========= HOURS =========

    /**
     * Save all hours tab form (in uberall and uska)
     * @param showSuccess show alert if request is successfully complete
     */
    saveHours(showSuccess: boolean, removedPeriods: IPeriodHours[], callback: () => void) {
      this.isLoading = true

      const alertStore = useAlertStore()

      if (this.currentEstablishment) {
        // remove periods
        const promises: Promise<any>[] = []

        removedPeriods.forEach((period) => {
          const promise = EstablishmentDataService.deleteEstablishmentPlannedPeriod(period.id!)
          promises.push(promise)
        })

        Promise.all(promises).then(() => {
          setTimeout(async () => {
            // save periods
            EstablishmentDataService.updateEstablishmentHours({
              ...this.form.hours,
              establishment_id: this.currentEstablishment.id,
            }).then(
              () => {
                useTrackingStore().trackDataUpdate(this.currentEstablishment.id, "hours")
                this.updatable(true, callback)

                if (showSuccess) {
                  alertStore.push(
                    AlertType.success,
                    i18n.global.t("establishments/formulaire.hours.success_alert_message"),
                    i18n.global.t("establishments/formulaire.hours.tab_title")
                  )
                }
              },
              (e) => {
                this.isLoading = false
                switch (e.response.status) {
                  default:
                    alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                    break
                }
              }
            )
          }, 1000)
        })
      }
    },

    // ========= MEDIAS =========

    loadAlbums() {
      if (!this.currentEstablishment.category_id) {
        this.albums = []
        this.albumsLoading = false
        return
      }

      this.albumsLoading = true

      MediaDataService.getCategoryAlbumsRules(this.currentEstablishment.category_id).then(
        (res) => {
          this.albums = res.data.data ?? []
          this.albumsLoading = false
        },
        (e) => {
          this.albumsLoading = false
          const alertStore = useAlertStore()

          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    /**
     * Upload new media for current establishment
     * @param album current selected album to upload new media
     * @param submitted file to upload
     */
    uploadMedias(album: MediaAlbum, submitted: File, callback?: () => void): Promise<any> {
      const alertStore = useAlertStore()
      if (this.currentEstablishment) {
        const payload: MediaPayload = {
          establishment_id: this.currentEstablishment!.id,
          album: album.code,
          file: submitted,
        }

        const authStore = useAuthenticationStore()

        const promise = MediaDataService.uploadMedia(payload, authStore.currentHeaders).then(
          () => {
            this.updatable(true, () => {
              this.getCurrentEstablishment()
            })

            if (callback) {
              callback()
            }
          },
          (e) => {
            switch (e.response.status) {
              case 401:
                break
              default:
                alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                break
            }
          }
        )

        return promise
      } else {
        return new Promise(() => {
          alertStore.push(AlertType.error, i18n.global.t("common.errors.no_establishment"))
        })
      }
    },

    setMediaRank(id: number, successCallback?: () => void) {
      MediaDataService.updateMediaRank(id).then(
        async () => {
          this.updatable(true, () => {
            if (successCallback) {
              successCallback()
            }
          })
        },
        (e) => {
          const alertStore = useAlertStore()

          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    deleteMedia(id: number, successCallback?: () => void) {
      MediaDataService.deleteMedia(id).then(
        async () => {
          this.updatable(true, () => {
            if (successCallback) {
              successCallback()
            }
          })
        },
        (e) => {
          const alertStore = useAlertStore()

          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    hasOverlapWithHours(plannedPeriods, schedule) {
      for (let i = 0; i < plannedPeriods.length; i++) {
        let current = plannedPeriods[i]
        let next = plannedPeriods[i + 1] || null

        let startDate = moment(current.start_date)
        const hasValidRange = Array.isArray(schedule.range) && schedule.range[0]
        let currentExceptionalStart = hasValidRange ? moment(schedule.range[0]) : null

        if (!next) {
          if (currentExceptionalStart && currentExceptionalStart.isSameOrAfter(startDate)) {
            return current.temporary_close === true || current.open_24_7 === true
          }
        } else {
          let endDate = moment(next.start_date)

          if (currentExceptionalStart && currentExceptionalStart.isBetween(startDate, endDate, null, "[)")) {
            return current.temporary_close === true || current.open_24_7 === true
          }
        }
      }

      return false
    },

    /**
     * Charge l'info si le SALE rattaché à l'établissement est d'un compte partenaire
     * et si l'établissement a des licences
     */
    async loadSalePartnerInfo() {
      if (!this.currentEstablishment?.id) return

      try {
        const response = await EstablishmentDataService.getEstablishmentSaleUser(
          this.currentEstablishment.id
        )
        if (response.data.success) {
          const linked = response.data.data.account_linked_to_sale
          this.accountLinkedToSale = typeof linked === "boolean" ? linked : null

          if (response.data.data.sale_user) {
            this.isSalePartnerAccount = response.data.data.sale_user.is_partner_account ?? null
            this.saleProductPlans = response.data.data.sale_user.product_plans ?? []
            this.saleTermsUrl = response.data.data.sale_user.terms_url ?? null
          } else {
            this.isSalePartnerAccount = null
            this.saleProductPlans = []
            this.saleTermsUrl = null
          }
          
          if (response.data.data.establishment) {
            this.hasEstablishmentLicenses = response.data.data.establishment.has_licenses ?? false
            // Récupérer la date de formation du compte de l'établissement
            this.establishmentAccountTrainingDate = response.data.data.establishment.account?.training_date ?? null
            // Récupérer les facilités de paiement du compte de l'établissement
            this.allowWireTransfer = response.data.data.establishment.allow_wire_transfer ?? null
            this.paymentFacilities = response.data.data.establishment.payment_facilities ?? null
            this.accountExternalBilling =
              response.data.data.establishment.external_billing === true ? true : false
          }
        } else {
        this.isSalePartnerAccount = null
        this.accountLinkedToSale = null
        this.saleProductPlans = []
        this.saleTermsUrl = null
        this.hasEstablishmentLicenses = false
        this.establishmentAccountTrainingDate = null
        this.allowWireTransfer = null
        this.paymentFacilities = null
        this.accountExternalBilling = null
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du SALE:', error)
      this.isSalePartnerAccount = null
      this.accountLinkedToSale = null
      this.saleProductPlans = []
      this.saleTermsUrl = null
      this.hasEstablishmentLicenses = false
      this.establishmentAccountTrainingDate = null
      this.allowWireTransfer = null
      this.paymentFacilities = null
      this.accountExternalBilling = null
    }
    },
  },

  persist: {
    strategies: [
      {
        paths: [
          "count",
          "showSaveButton",
          "currentTabIndex",
          "maxIndex",
          "configMaxIndex",
          "isConfigurationForm",
          "currentEstablishment",
          "currentEstablishmentIsUpdatable",
          "hasEstablishmentLicenses",
          "establishmentAccountTrainingDate",
          "form",
          "categories",
          "attributes",
          "availableAttributes",
          "paymentOptions",
          "languages",
          "lodgingLanguages",
          "albums",
          "isLoading",
          "inSyncing",
          "formLoaded",
        ],
      },
    ],
  },
})
